import React from 'react'
import * as PhosphorIcons from '@phosphor-icons/react'

const SpamAnalyzerViewResult = ({result}) => {
  return (
    <div className="mt-4 overflow-x-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Spam Analysis Results */}
        <div className="space-y-4">
          {/* Main Score Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <PhosphorIcons.ShieldCheck size={20} weight="bold" className="text-primary"/>
                </div>
                <h3 className="card-title text-lg">Spam Analysis</h3>
              </div>
              
              <div className="grid grid-cols-1 gap-4">
                {/* Spam Status */}
                <div className="flex items-center justify-between p-3 bg-base-200 rounded-lg">
                  <span className="font-medium">Status</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    result?.spam_analysis?.is_spam 
                      ? 'bg-error/20 text-error' 
                      : 'bg-success/20 text-success'
                  }`}>
                    {result?.spam_analysis?.is_spam ? 'Spam Detected' : 'Clean Content'}
                  </span>
                </div>

                {/* Spam Score */}
                <div className="p-3 bg-base-200 rounded-lg space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Spam Score</span>
                    <div className="flex gap-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        result?.spam_analysis?.spam_score?.risk_level === 'high' ? 'bg-error/20 text-error' :
                        result?.spam_analysis?.spam_score?.risk_level === 'medium' ? 'bg-warning/20 text-warning' :
                        'bg-success/20 text-success'
                      }`}>
                        {result?.spam_analysis?.spam_score?.risk_level} risk
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-info/20 text-info">
                        {Math.round(result?.spam_analysis?.spam_score?.confidence * 100)}% confidence
                      </span>
                    </div>
                  </div>
                  <div className="w-full bg-base-300 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        result?.spam_analysis?.spam_score?.value > 0.7 ? 'bg-error' :
                        result?.spam_analysis?.spam_score?.value > 0.3 ? 'bg-warning' :
                        'bg-success'
                      }`}
                      style={{width: `${result?.spam_analysis?.spam_score?.value * 100}%`}}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Primary Indicators */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-success/10">
                  <PhosphorIcons.ListChecks size={20} weight="bold" className="text-success"/>
                </div>
                <h3 className="card-title text-lg">Primary Indicators</h3>
              </div>
              
              <div className="space-y-2">
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium capitalize">
                      {result?.spam_analysis?.detection_factors?.primary_indicators?.category?.replace(/_/g, ' ')}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
                      {Math.round(result?.spam_analysis?.detection_factors?.primary_indicators?.confidence * 100)}% confidence
                    </span>
                  </div>
                  <div className="space-y-1">
                    {result?.spam_analysis?.detection_factors?.primary_indicators?.evidence?.map((evidence, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm text-base-content/70">
                        <PhosphorIcons.Check size={16} className="text-success"/>
                        <span>{evidence}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Metadata and Stats */}
        <div className="space-y-4">
          {/* Content Stats */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-secondary/10">
                  <PhosphorIcons.ChartBar size={20} weight="bold" className="text-secondary"/>
                </div>
                <h3 className="card-title text-lg">Content Statistics</h3>
              </div>
              
              <div className="grid grid-cols-3 gap-2">
                <div className="stat bg-base-200 rounded-lg p-3">
                  <div className="stat-title text-xs">Text Length</div>
                  <div className="stat-value text-lg">
                    {result?.metadata?.content_stats?.text_length || 0}
                  </div>
                </div>
                <div className="stat bg-base-200 rounded-lg p-3">
                  <div className="stat-title text-xs">URLs</div>
                  <div className="stat-value text-lg">
                    {result?.metadata?.content_stats?.url_count || 0}
                  </div>
                </div>
                <div className="stat bg-base-200 rounded-lg p-3">
                  <div className="stat-title text-xs">Caps %</div>
                  <div className="stat-value text-lg">
                    {Math.round(result?.metadata?.content_stats?.caps_percentage * 100)}%
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Suspicious Elements */}
          {result?.spam_analysis?.detection_factors?.suspicious_elements?.length > 0 && (
            <div className="card bg-base-100 shadow-sm">
              <div className="card-body p-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="p-2 rounded-lg bg-error/10">
                    <PhosphorIcons.Warning size={20} weight="bold" className="text-error"/>
                  </div>
                  <h3 className="card-title text-lg">Suspicious Elements</h3>
                </div>
                
                <div className="space-y-2">
                  {result?.spam_analysis?.detection_factors?.suspicious_elements?.map((element, index) => (
                    <div key={index} className="p-3 bg-base-200 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{element.type}</span>
                        <span className="px-2 py-0.5 rounded-full text-xs bg-error/20 text-error">
                          {Math.round(element.severity * 100)}% severity
                        </span>
                      </div>
                      <p className="text-sm mt-1 text-base-content/70">{element.details}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Analysis Metrics */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-info/10">
                  <PhosphorIcons.Gauge size={20} weight="bold" className="text-info"/>
                </div>
                <h3 className="card-title text-lg">Analysis Metrics</h3>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between p-2 bg-base-200 rounded-lg">
                  <span>Overall Confidence</span>
                  <span className="font-medium">
                    {Math.round(result?.metadata?.analysis_metrics?.overall_confidence * 100)}%
                  </span>
                </div>
                <div className="flex justify-between p-2 bg-base-200 rounded-lg">
                  <span>False Positive Risk</span>
                  <span className="font-medium">
                    {Math.round(result?.metadata?.analysis_metrics?.false_positive_risk * 100)}%
                  </span>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpamAnalyzerViewResult